/** Geometría del grid de canchas (Calendario del torneo y Canchas del club).
 *
 * Filas de 15 minutos y UNA tarjeta por partido/bloqueo que abarca las filas
 * de su duración, como un calendario de Google (pedido Eduardo 2026-10-01:
 * "hay partidos que empiezan 5:15 p.m."). Todo se calcula con INTERVALOS
 * semiabiertos [inicio, fin): un partido de 17:00 con bloque de 90 ocupa
 * 17:00–18:30 y un inicio a las 18:30 ya no choca.
 *
 * Gate #146 de Hermes (sigue vigente): un partido NUNCA desaparece del grid
 * aunque su hora no caiga en una fila "redonda" (17:30, 17:10…) o quede fuera
 * de Desde–Último inicio — el rango del grid se estira para incluirlo — y lo
 * que se ve es exactamente lo que bloquea un inicio.
 */

import { COURT_HALVES, courtsOverlap, parseHalfCourt, resolveCourtColumn, type CourtHalf } from "./courts";
import { isActiveCalendarStatus } from "./matchScheduleStatus";

/** Alto lógico de cada fila del grid. */
export const CALENDAR_ROW_MINUTES = 15;

const DAY_MINUTES = 24 * 60;

export function minutesFromTime(value: string): number {
  const [h, m] = value.split(":").map(Number);
  if (!Number.isFinite(h) || !Number.isFinite(m)) return 0;
  return h * 60 + m;
}

/** Como minutesFromTime pero sin inventar 00:00: null si la hora no se
 * entiende (así un dato raro no se pinta a medianoche). */
export function parseTimeMinutes(value: string | null | undefined): number | null {
  if (!value) return null;
  const match = /^\s*(\d{1,2}):(\d{2})/.exec(value);
  if (!match) return null;
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (hours > 23 || minutes > 59) return null;
  return hours * 60 + minutes;
}

export function timeFromMinutes(totalMinutes: number): string {
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

/** "16:00" → "4:00 p. m." (Eduardo 2026-08-28: el picker nativo confundía). */
export function friendlyTime12h(value: string): string {
  const [hours, minutes] = value.split(":").map(Number);
  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return value;
  const suffix = hours < 12 ? "a. m." : "p. m.";
  const hour12 = hours % 12 === 0 ? 12 : hours % 12;
  return `${hour12}:${String(minutes).padStart(2, "0")} ${suffix}`;
}

/** "17:00–18:30" para títulos y avisos. */
export function intervalLabel(startMin: number, endMin: number): string {
  return `${timeFromMinutes(startMin)}–${timeFromMinutes(Math.min(endMin, DAY_MINUTES))}`;
}

// Inicios cada 15 minutos de 6:00 a. m. a 11:45 p. m. — rango real de canchas.
export const CALENDAR_TIME_OPTIONS: Array<{ value: string; label: string }> = Array.from(
  { length: (23 * 60 + 45 - 6 * 60) / CALENDAR_ROW_MINUTES + 1 },
  (_, index) => {
    const value = timeFromMinutes(6 * 60 + index * CALENDAR_ROW_MINUTES);
    return { value, label: friendlyTime12h(value) };
  },
);

/** Opciones del select conservando un valor guardado fuera de la retícula
 * (p. ej. "05:30" del horario de un club). */
export function calendarTimeOptionsFor(value: string): Array<{ value: string; label: string }> {
  if (!value || CALENDAR_TIME_OPTIONS.some((option) => option.value === value)) {
    return CALENDAR_TIME_OPTIONS;
  }
  return [{ value, label: friendlyTime12h(value) }, ...CALENDAR_TIME_OPTIONS];
}

/** Duración que viaja al servidor para revisar choques por intervalo: solo
 * valores que el backend acepta (10–600); fuera de eso se omite y el
 * servidor conserva su revisión de siempre (no hay rechazos nuevos). */
export function durationForServer(minutes: number): number | undefined {
  return Number.isInteger(minutes) && minutes >= 10 && minutes <= 600 ? minutes : undefined;
}

export interface OccupancyCandidate {
  scheduled_time: string | null;
  court: string | null;
  status: string;
  scheduled_date: string | null;
}

/** Inventario de OCUPACIÓN de una fecha: cualquier partido ACTIVO con hora
 * ocupa cancha — SIN filtros de presentación (gate #146: aplicar el filtro
 * ladder de la división ACTIVA al inventario foreign eliminaba los
 * "Round of N" de una división elimination y la celda parecía libre). */
export function occupancyInventory<M extends OccupancyCandidate>(
  matches: M[],
  date: string,
): M[] {
  return matches.filter(
    (match) =>
      isActiveCalendarStatus(match.status) &&
      match.scheduled_date === date &&
      Boolean(match.scheduled_time),
  );
}

/** Llave de sede como la del backend (_venue_key): sin acentos, sin
 * mayúsculas y con espacios compactados. */
export function venueKey(value: string | null | undefined): string {
  const cleaned = (value ?? "").trim().split(/\s+/).join(" ");
  if (!cleaned) return "";
  return cleaned.normalize("NFKD").replace(/[̀-ͯ]/g, "").toLocaleLowerCase("es-MX");
}

// ---- intervalos y tarjetas ---------------------------------------------------

export interface CourtGridInterval {
  key: string;
  /** Cancha ya recortada (trim). Las mini canchas ("9-A") se dibujan en la
   * columna "9" si no está dividida (ver resolveCourtColumn). */
  court: string;
  startMin: number;
  endMin: number;
  /** Para assignLanes: cancha real de la tarjeta cuando `court` es solo el
   * grupo de carriles (mini canchas 9-A y 9-B lado a lado en la columna 9
   * no chocan). Sin ella se usa `court`. */
  sourceCourt?: string;
}

/** own = partido de la vista (seleccionable); own-hidden = del torneo pero
 * fuera de los filtros (seleccionable, atenuado); division = otra categoría o
 * fuera de la vista (solo lectura); club = otro torneo del club (solo
 * lectura); block = bloqueo de cancha; reservation = reserva del club. */
export type CourtGridItemKind = "own" | "own-hidden" | "division" | "club" | "block" | "reservation";

export interface CourtGridItem<P = unknown> extends CourtGridInterval {
  kind: CourtGridItemKind;
  /** Id del partido (null en bloqueos/reservas): al mover un partido su
   * propia tarjeta no le estorba. */
  matchId: number | null;
  payload: P;
}

/** [hora, hora+duración) de un partido; null sin hora válida. */
export function matchGridInterval(
  scheduledTime: string | null | undefined,
  durationMinutes: number,
): { startMin: number; endMin: number } | null {
  const startMin = parseTimeMinutes(scheduledTime);
  if (startMin === null) return null;
  return { startMin, endMin: startMin + Math.max(1, durationMinutes) };
}

export function intervalsOverlap(
  a: { startMin: number; endMin: number },
  b: { startMin: number; endMin: number },
): boolean {
  return a.startMin < b.endMin && b.startMin < a.endMin;
}

export interface GridRange {
  /** Minuto de la primera fila (múltiplo de 15). */
  startMin: number;
  /** Minuto donde termina la última fila (exclusivo). */
  endMin: number;
  rowMinutes: number;
  /** Minuto de inicio de cada fila. */
  rows: number[];
}

/** Rango visible del grid: de Desde (o el primer partido, si empieza antes)
 * hasta Último inicio + duración (o el fin del último partido, si termina
 * después). Así ningún partido queda fuera del grid (gate #146). Los
 * `extendWith` deben ser solo los partidos de las canchas visibles; los
 * bloqueos/reservas no estiran el grid (se recortan). */
export function buildGridRange(options: {
  windowStartMin: number;
  windowEndMin: number;
  durationMinutes: number;
  extendWith?: Array<{ startMin: number; endMin: number }>;
  rowMinutes?: number;
}): GridRange | null {
  const rowMinutes = options.rowMinutes ?? CALENDAR_ROW_MINUTES;
  const { windowStartMin, windowEndMin } = options;
  if (!Number.isFinite(windowStartMin) || !Number.isFinite(windowEndMin) || windowEndMin < windowStartMin) {
    return null;
  }
  const duration = Number.isFinite(options.durationMinutes) && options.durationMinutes > 0
    ? options.durationMinutes
    : rowMinutes;
  let first = windowStartMin;
  let last = windowEndMin + duration;
  for (const item of options.extendWith ?? []) {
    first = Math.min(first, item.startMin);
    last = Math.max(last, item.endMin);
  }
  const startMin = Math.max(0, Math.floor(first / rowMinutes) * rowMinutes);
  const endMin = Math.min(DAY_MINUTES, Math.ceil(last / rowMinutes) * rowMinutes);
  if (endMin <= startMin) return null;
  const rows: number[] = [];
  for (let t = startMin; t < endMin; t += rowMinutes) rows.push(t);
  return { startMin, endMin, rowMinutes, rows };
}

/** Fila (0 = primera) y número de filas que abarca una tarjeta: el inicio se
 * redondea hacia abajo y el fin hacia arriba (17:10–18:40 → filas 17:00 a
 * 18:30). Lo que cae fuera del rango se recorta; null si no se ve nada.
 *
 * `offsetTop`/`offsetBottom` (fracción de fila, 0–1) dicen cuánto de la
 * primera/última fila NO ocupa: una tarjeta de 08:50 empieza a 2/3 de la fila
 * de 08:45. Así dos partidos seguidos con bloques que no son múltiplo de 15
 * (08:00–08:50 y 08:50–09:40) se dibujan uno tras otro, no encimados. */
export function rowPlacement(
  startMin: number,
  endMin: number,
  range: GridRange,
): { row: number; span: number; offsetTop: number; offsetBottom: number } | null {
  const rowMinutes = range.rowMinutes;
  const rawStart = Math.floor((startMin - range.startMin) / rowMinutes);
  const rawEnd = Math.ceil((endMin - range.startMin) / rowMinutes);
  const row = Math.max(0, rawStart);
  const rowEnd = Math.min(range.rows.length, Math.max(rawEnd, rawStart + 1));
  if (rowEnd <= row) return null;
  const span = rowEnd - row;
  const firstRowStart = range.startMin + row * rowMinutes;
  const lastRowEnd = range.startMin + rowEnd * rowMinutes;
  // Recortada arriba/abajo: llena la fila de la orilla.
  let offsetTop = rawStart < 0 ? 0 : Math.max(0, (startMin - firstRowStart) / rowMinutes);
  let offsetBottom = rawEnd > range.rows.length ? 0 : Math.max(0, (lastRowEnd - endMin) / rowMinutes);
  // Nunca menos de media fila visible (un dato raro de 1 min sigue tocable).
  const visible = span - offsetTop - offsetBottom;
  if (visible < 0.5) {
    const excess = 0.5 - visible;
    const fromBottom = Math.min(offsetBottom, excess);
    offsetBottom -= fromBottom;
    offsetTop = Math.max(0, offsetTop - (excess - fromBottom));
  }
  return { row, span, offsetTop, offsetBottom };
}

export interface LaneInfo {
  lane: number;
  lanes: number;
  /** Se traslapa (en tiempo real) con otra tarjeta de la misma cancha. */
  conflict: boolean;
}

/** Carriles por cancha: dos tarjetas que se enciman se dibujan lado a lado
 * (cada grupo encimado comparte el número de carriles). Como las tarjetas se
 * dibujan con sus minutos reales (ver rowPlacement), el carril y el
 * conflicto usan los MISMOS minutos reales: dos partidos seguidos de 50 min
 * (08:00–08:50 y 08:50–09:40) van en un solo carril, y solo un traslape de
 * verdad parte la columna.
 *
 * Conflicto = se encima en tiempo con otra tarjeta del grupo cuya cancha
 * real (`sourceCourt`) comparte espacio: las mini canchas 9-A, 9-B (… 9-D) a la
 * misma hora van lado a lado sin marcarse en rojo; 9 y 9-A sí chocan. */
export function assignLanes(items: CourtGridInterval[]): Map<string, LaneInfo> {
  const result = new Map<string, LaneInfo>();
  const byCourt = new Map<string, CourtGridInterval[]>();
  for (const item of items) {
    const list = byCourt.get(item.court) ?? [];
    list.push(item);
    byCourt.set(item.court, list);
  }
  for (const list of byCourt.values()) {
    const sorted = list
      .map((item) => ({ item, start: item.startMin, end: Math.max(item.endMin, item.startMin + 1) }))
      .sort((a, b) => a.start - b.start || b.end - a.end || a.item.key.localeCompare(b.item.key));
    type Entry = (typeof sorted)[number];
    let cluster: Array<{ entry: Entry; lane: number }> = [];
    let laneEnds: number[] = [];
    let clusterEnd = -Infinity;
    const closeCluster = () => {
      for (const current of cluster) {
        const source = current.entry.item.sourceCourt ?? current.entry.item.court;
        const conflict = cluster.some(
          (other) =>
            other !== current &&
            other.entry.start < current.entry.end &&
            current.entry.start < other.entry.end &&
            courtsOverlap(source, other.entry.item.sourceCourt ?? other.entry.item.court),
        );
        result.set(current.entry.item.key, { lane: current.lane, lanes: laneEnds.length, conflict });
      }
      cluster = [];
      laneEnds = [];
    };
    for (const entry of sorted) {
      if (entry.start >= clusterEnd) closeCluster();
      let lane = laneEnds.findIndex((end) => end <= entry.start);
      if (lane === -1) {
        lane = laneEnds.length;
        laneEnds.push(entry.end);
      } else {
        laneEnds[lane] = entry.end;
      }
      cluster.push({ entry, lane });
      clusterEnd = Math.max(clusterEnd, entry.end);
    }
    closeCluster();
  }
  return result;
}

/** Cómo se dibuja una tarjeta en el grid de canchas. */
export interface CourtGridLayout extends LaneInfo {
  /** Primera columna (índice en `columns`). */
  column: number;
  /** Columnas que abarca (2..4 = cancha completa sobre sus mini canchas). */
  columnSpan: number;
  /** Mini cancha de la tarjeta ("A".."D") o null si es cancha completa. */
  half: CourtHalf | null;
  /** La tarjeta mide menos que una columna normal (media columna o carril):
   * la página la dibuja compacta. */
  narrow: boolean;
}

/** Acomoda las tarjetas en las columnas del grid (mini canchas, Eduardo
 * 2026-10-01). Cada tarjeta va donde resolveCourtColumn dice; las que no
 * caben en ninguna columna no van (la página las cuenta y avisa).
 *
 * Los carriles se calculan por cancha FÍSICA (la columna, o las dos
 * columnas de una cancha dividida). Si en un grupo encimado nada choca de
 * verdad, cada tarjeta va en su lugar natural: la mini cancha A en la mitad
 * izquierda de la columna 9 sin dividir y la B en la derecha; la cancha
 * completa abarca sus dos mini canchas. Si algo choca de verdad (datos
 * raros: una 9 completa encima de una 9-A), TODO ese grupo se reparte en
 * carriles a lo ancho de la cancha física y lo que choca se marca: nada se
 * esconde debajo de otra tarjeta (gate #146). */
export function layoutCourtGridItems(items: CourtGridInterval[], columns: string[]): Map<string, CourtGridLayout> {
  const halfColumn = columns.map((value) => parseHalfCourt(value));
  const columnIndex = new Map<string, number>();
  halfColumn.forEach((half, index) => {
    if (half && !columnIndex.has(`${half.base}-${half.half}`)) columnIndex.set(`${half.base}-${half.half}`, index);
  });
  /** Cancha física de una columna: sus mini canchas juntas (A, B y, si hay,
   * C y D) o la columna sola. */
  const physicalOf = (column: number): { group: string; start: number; width: number } => {
    const half = halfColumn[column];
    if (half) {
      const left = columnIndex.get(`${half.base}-A`);
      if (left !== undefined && columnIndex.get(`${half.base}-B`) === left + 1) {
        let width = 2;
        while (width < COURT_HALVES.length && columnIndex.get(`${half.base}-${COURT_HALVES[width]}`) === left + width) {
          width += 1;
        }
        return { group: `g:${half.base}`, start: left, width };
      }
    }
    return { group: `c:${column}`, start: column, width: 1 };
  };
  type Placed = {
    item: CourtGridInterval;
    column: number;
    columnSpan: number;
    fixedLane?: number;
    physical: { group: string; start: number; width: number };
  };
  const placed: Placed[] = [];
  for (const item of items) {
    const resolved = resolveCourtColumn(item.court, columns);
    if (!resolved) continue;
    placed.push({
      item,
      column: resolved.column,
      columnSpan: resolved.span,
      fixedLane: resolved.fixedLane,
      physical: physicalOf(resolved.column),
    });
  }
  // Carriles genéricos por cancha física (con el choque por cancha real).
  const generic = assignLanes(
    placed.map((entry) => ({
      key: entry.item.key,
      court: entry.physical.group,
      sourceCourt: entry.item.court,
      startMin: entry.item.startMin,
      endMin: entry.item.endMin,
    })),
  );
  // Grupos encimados por cancha física: si en uno algo choca de verdad,
  // todo el grupo usa los carriles genéricos.
  const byGroup = new Map<string, Placed[]>();
  for (const entry of placed) {
    const list = byGroup.get(entry.physical.group) ?? [];
    list.push(entry);
    byGroup.set(entry.physical.group, list);
  }
  const clashKeys = new Set<string>();
  for (const list of byGroup.values()) {
    const sorted = [...list].sort(
      (a, b) => a.item.startMin - b.item.startMin || a.item.key.localeCompare(b.item.key),
    );
    let cluster: Placed[] = [];
    let clusterEnd = -Infinity;
    const closeCluster = () => {
      if (cluster.some((entry) => generic.get(entry.item.key)?.conflict)) {
        for (const entry of cluster) clashKeys.add(entry.item.key);
      }
      cluster = [];
    };
    for (const entry of sorted) {
      if (entry.item.startMin >= clusterEnd) closeCluster();
      cluster.push(entry);
      clusterEnd = Math.max(clusterEnd, Math.max(entry.item.endMin, entry.item.startMin + 1));
    }
    closeCluster();
  }
  // Mini canchas en una columna sin dividir: tantos carriles como la letra
  // más alta en esa columna (A/B → 2; con C → 3; con D → 4), así en una 9
  // dividida en 3 las tarjetas A, B y C miden un tercio y no se enciman.
  const fixedLanesByColumn = new Map<number, number>();
  for (const entry of placed) {
    if (entry.fixedLane === undefined) continue;
    fixedLanesByColumn.set(entry.column, Math.max(fixedLanesByColumn.get(entry.column) ?? 2, entry.fixedLane + 1));
  }
  const result = new Map<string, CourtGridLayout>();
  for (const entry of placed) {
    const key = entry.item.key;
    const half = parseHalfCourt(entry.item.court)?.half ?? null;
    if (clashKeys.has(key)) {
      const lanes = generic.get(key) ?? { lane: 0, lanes: 1, conflict: false };
      result.set(key, {
        ...lanes,
        column: entry.physical.start,
        columnSpan: entry.physical.width,
        half,
        narrow: lanes.lanes > 1,
      });
    } else if (entry.fixedLane !== undefined) {
      result.set(key, {
        lane: entry.fixedLane,
        lanes: fixedLanesByColumn.get(entry.column) ?? 2,
        conflict: false,
        column: entry.column,
        columnSpan: 1,
        half,
        narrow: true,
      });
    } else {
      result.set(key, {
        lane: 0,
        lanes: 1,
        conflict: false,
        column: entry.column,
        columnSpan: entry.columnSpan,
        half,
        // Mini cancha en su propia columna: media columna (o menos) de ancho.
        narrow: entry.columnSpan === 1 && entry.physical.width > 1,
      });
    }
  }
  return result;
}

export type StartAvailability<I> =
  | { ok: true }
  | { ok: false; reason: "window" }
  | { ok: false; reason: "blocked" | "conflict"; item: I };

/** ¿Puede EMPEZAR aquí algo de `durationMinutes`? Revisa [inicio, inicio +
 * duración) contra todo lo de esa cancha (partidos propios, de otras
 * categorías, de otros torneos del club, bloqueos y reservas). El partido
 * que se está moviendo (`excludeMatchId`) no se estorba a sí mismo: así se
 * puede recorrer de 17:00 a 17:15 en su misma cancha.
 *
 * Mini canchas: cuenta todo lo que comparte espacio (courtsOverlap, como el
 * servidor): la 9-A libre = nada en 9 ni en 9-A; la 9 libre = nada en 9 ni
 * en 9-A..9-D. */
export function startAvailability<I extends CourtGridItem>(
  court: string,
  startMin: number,
  durationMinutes: number,
  items: I[],
  options: { excludeMatchId?: number | null; windowStartMin?: number; windowEndMin?: number } = {},
): StartAvailability<I> {
  if (
    (options.windowStartMin !== undefined && startMin < options.windowStartMin) ||
    (options.windowEndMin !== undefined && startMin > options.windowEndMin)
  ) {
    return { ok: false, reason: "window" };
  }
  const candidate = { startMin, endMin: startMin + Math.max(1, durationMinutes) };
  let found: I | null = null;
  for (const item of items) {
    if (!courtsOverlap(item.court, court)) continue;
    if (options.excludeMatchId != null && item.matchId === options.excludeMatchId) continue;
    if (!intervalsOverlap(candidate, item)) continue;
    if (!found || item.startMin < found.startMin) found = item;
  }
  if (!found) return { ok: true };
  return { ok: false, reason: found.kind === "block" ? "blocked" : "conflict", item: found };
}

/** Inicios para el llenado automático del club: de Desde en pasos de la
 * duración (+ descanso) hasta Último inicio, como el backend (bulk). */
export function steppedStarts(
  windowStartMin: number,
  windowEndMin: number,
  durationMinutes: number,
  bufferMinutes: number = 0,
): number[] {
  const step = durationMinutes + Math.max(0, bufferMinutes);
  if (!Number.isFinite(step) || step <= 0 || windowEndMin < windowStartMin) return [];
  const starts: number[] = [];
  for (let t = windowStartMin; t <= windowEndMin; t += step) starts.push(t);
  return starts;
}
